import React, { useEffect, useRef } from 'react';
import { Badge, Button, FilterMenu, SearchField, Segmented } from '../kit';
import { EMPTY_COPY, STATUSES, kindLabel, metaLine } from './skillsLogic';

/**
 * Left pane: status segments with counts, search, Kind filter, and the list.
 * ↑/↓ inside the list moves the selection (the page guards unsaved edits).
 */
export default function SkillList({
  items, counts, status, onStatus, query, onQuery, kinds, onKinds, kindOptions,
  selectedId, onSelect, onMove, lookups, loading, error, onRetry, filtered, truncated,
}) {
  const listRef = useRef(null);
  const focusPending = useRef(false);

  // After a keyboard move, keep focus on the newly selected row.
  useEffect(() => {
    if (!focusPending.current || !listRef.current) return;
    focusPending.current = false;
    const el = [...listRef.current.querySelectorAll('[data-skill-id]')]
      .find((node) => node.dataset.skillId === String(selectedId));
    if (el) { el.focus(); el.scrollIntoView({ block: 'nearest' }); }
  }, [selectedId]);

  const onKeyDown = (e) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    focusPending.current = true;
    onMove(e.key === 'ArrowDown' ? 1 : -1);
  };

  const empty = EMPTY_COPY[status] || { title: 'No skills', body: '' };

  return (
    <div className="ak-split-list">
      <div className="sk-list-tools">
        <Segmented
          mode="choice"
          fill
          label="Status"
          value={status}
          onChange={onStatus}
          options={STATUSES.map((s) => ({ value: s.value, label: s.label, count: loading ? null : counts[s.value] || 0 }))}
        />
        <div className="sk-list-search">
          <SearchField value={query} onChange={onQuery} placeholder="Search skills" label="Search skills" />
          {kindOptions.length ? (
            <FilterMenu label="Kind" options={kindOptions} value={kinds} onChange={onKinds} />
          ) : null}
        </div>
      </div>
      <div className="sk-list-scroll" ref={listRef} onKeyDown={onKeyDown} role="list" aria-label="Skills" aria-busy={loading || undefined}>
        {loading ? (
          Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="sk-item-sk" aria-hidden="true">
              <div className="ak-sk" style={{ width: `${55 + ((i * 17) % 35)}%` }} />
              <div className="ak-sk" style={{ width: `${35 + ((i * 11) % 25)}%`, height: 10 }} />
            </div>
          ))
        ) : error ? (
          <div className="ak-empty">
            <strong>Couldn&apos;t load skills</strong>
            {error}
            <div style={{ marginTop: 12 }}><Button onClick={onRetry}>Try again</Button></div>
          </div>
        ) : items.length === 0 ? (
          filtered ? (
            <div className="ak-empty">
              <strong>No matches</strong>
              Nothing in {STATUSES.find((s) => s.value === status)?.label.toLowerCase()} matches the search or kind filter.
            </div>
          ) : (
            <div className="ak-empty">
              <strong>{empty.title}</strong>
              {empty.body}
            </div>
          )
        ) : (
          items.map((s) => (
            <div role="listitem" key={s.id}>
              <button
                type="button"
                className="ak-list-item"
                data-skill-id={s.id}
                aria-current={s.id === selectedId ? 'true' : undefined}
                onClick={() => onSelect(s.id)}
              >
                <span className="sk-item-row">
                  <span className="sk-item-title">{s.title}</span>
                  <Badge>{kindLabel(s.kind)}</Badge>
                </span>
                <span className="sk-item-meta">{metaLine(s, lookups)}</span>
              </button>
            </div>
          ))
        )}
      </div>
      {truncated && !loading ? (
        <div className="sk-list-foot">Showing the most recently updated 500 skills.</div>
      ) : null}
    </div>
  );
}
